import { useEffect, useRef, useState } from 'react';
import { currentTokens, handleCallback, loginRedirect } from './auth';
import { loadConfig } from './config';
import {
  decisionsApi,
  getRunwaySnapshot,
  overridesApi,
  pipelineApi,
  projectsApi,
  sendChat,
} from './api';
import type {
  DecisionRow,
  PipelineRow,
  ProjectRow,
  RunwayScenario,
  RunwaySnapshot,
} from './api';

/* =====================================================================
   COMPASS — Strategic command for Jeffrey Enneking
   Ported from reference/atelier.jsx. Backend swaps:
     window.storage  → /api/{pipeline,projects,decisions,financial-overrides}
     direct Anthropic → /api/chat
   Auth: Cognito Hosted UI redirect + token refresh handled in ./auth.
   ===================================================================== */

// ---------- Seed (first-load fallback if backend tables are empty) ----------
// Mirrors SEED in reference/atelier.jsx lines 21-97. Pushed to the API on
// first boot when the corresponding table returns no items.

const SEED_PROJECTS: ProjectRow[] = [
  { id: 'hs', name: 'Houston Scanning', tier: 'production', status: 'live',
    mrr: 0, customer: 'Danny (brother)', stack: 'Lambda + Rekognition',
    next: 'Formalize customer agreement (Josh draft)',
    notes: '8,864 clients · 10K photos/mo · primary §174A foundation' },
  { id: 'dg', name: 'DG Locations', tier: 'production', status: 'live',
    mrr: 0, customer: 'Dez (Local 399)', stack: 'React/Vite + Lambda',
    next: 'Disney scope review with Josh before promoting',
    notes: "Dez's freelance Loc Mgr tooling" },
  { id: 'ff', name: 'Forte Flavors', tier: 'commercial', status: 'in-dev',
    mrr: 0, customer: 'Janet/Lisa/Stacy (Forte Flavors LLC)', stack: 'React/Vite + Lambda',
    next: 'Marketing module 9A-9F', notes: 'Largest commercial deal pending' },
  { id: 'fl', name: 'Flavorist', tier: 'commercial', status: 'in-dev',
    mrr: 0, customer: 'Jeff Thomas (Alpha Innovations)', stack: 'React/Vite + Lambda',
    next: 'Formula Studio MVP', notes: "Father-in-law's supplement business" },
  { id: 'ew', name: 'Enneking Wealth / HQ', tier: 'platform', status: 'live',
    mrr: 0, customer: 'Self', stack: 'React/Vite + Plaid + Lambda',
    next: 'Expose API for Compass to consume',
    notes: 'Source of truth for finances · powers Compass runway' },
  { id: 'dezmm', name: 'The Dez MM (5 SaaS)', tier: 'commercial', status: 'in-dev',
    mrr: 0, customer: 'SMBs (TBD)', stack: 'React/Vite + Lambda + Stripe',
    next: 'Pick the strongest 1-2 to ship first',
    notes: 'ContractLens / InvoiceIQ / ProposalForge / RepurposeEngine / LeaseLogic' },
  { id: 'sl', name: 'Shards Ledger', tier: 'commercial', status: 'paused',
    mrr: 0, customer: 'TV production accounting', stack: 'React/Vite',
    next: 'Disney scope decision before any work resumes',
    notes: '⚠ Highest Disney IP risk · quarantined' },
  { id: 'pa', name: 'PolyAgent', tier: 'platform', status: 'paper-trading',
    mrr: 0, customer: 'Self', stack: 'Lambda + Polymarket API',
    next: 'Live trading after liquidity farming validation',
    notes: '$10K → $100K through 6 risk tiers' },
  { id: 'br', name: 'BrainOS', tier: 'platform', status: 'live',
    mrr: 0, customer: 'Self (multi-agent dev)', stack: 'Claude Code + custom orchestration',
    next: 'Continuous improvement', notes: 'Personal infrastructure · ~/Projects/BrainOS' },
];

const SEED_PIPELINE: PipelineRow[] = [
  { id: 'p1', source: 'Houston Scanning', kind: 'SaaS revenue', value: 1000, prob: 0.9, when: '30d', next: 'Per-photo billing live', tone: 'ok' },
  { id: 'p2', source: 'Forte Flavors', kind: 'SaaS contract', value: 24000, prob: 0.5, when: '90d', next: 'Pricing proposal · Janet decision', tone: 'watch' },
  { id: 'p3', source: 'Flavorist', kind: 'SaaS contract', value: 18000, prob: 0.7, when: '60d', next: 'Formula Studio demo for Jeff', tone: 'ok' },
  { id: 'p4', source: 'TV production network', kind: 'Consulting', value: 15000, prob: 0.6, when: '45d', next: 'Reach out to Lori, Maya, prod office contacts', tone: 'watch' },
  { id: 'p5', source: 'Disney severance', kind: 'One-time', value: 23000, prob: 0.5, when: '30d', next: 'Conditional on layoff timing & terms', tone: 'watch' },
  { id: 'p6', source: 'CA unemployment', kind: 'Bridge income', value: 11700, prob: 0.85, when: '30d', next: 'File immediately if separated', tone: 'ok' },
];

const SEED_DECISIONS: DecisionRow[] = [
  { id: 'd1', date: '2026-04-25', title: 'Commencement date = June 2023',
    reasoning: 'First Glide invoice. Maximizes §174A retroactive election scope (covers 2023, 2024, 2025).',
    verdict: 'Bob to confirm', tone: 'pending' },
  { id: 'd2', date: '2026-04-25', title: 'No retroactive invoicing of Houston Scanning',
    reasoning: 'Backdating invoices to a relative reads as fraud. Better narrative: "first production deployment of platform."',
    verdict: 'Decided', tone: 'firm' },
  { id: 'd3', date: '2026-04-25', title: 'LLC stays single-member',
    reasoning: 'CA community property already covers Dez. Adding her as member adds partnership-tax complexity for zero gain.',
    verdict: 'Decided · revisit at S-Corp threshold', tone: 'firm' },
  { id: 'd4', date: '2026-04-25', title: 'Disney work stays on personal AWS for now',
    reasoning: 'Migrating right before §174A filing looks like consciousness of guilt. Disclose through Josh, get clean handoff plan.',
    verdict: 'Decided', tone: 'firm' },
];

// ---------- Helpers ----------
const fmt = (n: number | null | undefined): string => {
  if (n === Number.POSITIVE_INFINITY) return '∞';
  if (n == null || Number.isNaN(n) || !Number.isFinite(n)) return '—';
  return Math.round(n).toLocaleString('en-US');
};
const fmtShort = (n: number | null | undefined): string => {
  if (n == null || Number.isNaN(n) || !Number.isFinite(n)) return '—';
  if (Math.abs(n) >= 1e6) return (n / 1e6).toFixed(2) + 'M';
  if (Math.abs(n) >= 1e3) return (n / 1e3).toFixed(1) + 'k';
  return Math.round(n).toString();
};
const monthsLabel = (m: number | null | undefined): string => {
  if (m === Number.POSITIVE_INFINITY || m === null) return '∞';
  if (m == null || Number.isNaN(m)) return '—';
  return m.toFixed(1) + 'mo';
};

// ---------- Auth/boot wrapper ----------
type BootState =
  | { kind: 'loading' }
  | { kind: 'unauthenticated' }
  | { kind: 'authenticated' }
  | { kind: 'error'; message: string };

export function App() {
  const [state, setState] = useState<BootState>({ kind: 'loading' });

  useEffect(() => {
    void boot(setState);
  }, []);

  if (state.kind === 'loading') {
    return (
      <main className="boot-shell">
        <div className="boot-card">
          <div className="eyebrow">Compass</div>
          <h1>Loading…</h1>
        </div>
      </main>
    );
  }
  if (state.kind === 'error') {
    return (
      <main className="boot-shell">
        <div className="boot-card">
          <div className="eyebrow">Compass · setup incomplete</div>
          <h1>Configuration missing</h1>
          <p>{state.message}</p>
        </div>
      </main>
    );
  }
  if (state.kind === 'unauthenticated') {
    return (
      <main className="boot-shell">
        <div className="boot-card">
          <div className="eyebrow">Compass</div>
          <h1>Sign in</h1>
          <p>Strategic command for the unbounded life.</p>
          <button className="btn-primary" onClick={() => void loginRedirect()}>
            Continue with Cognito
          </button>
        </div>
      </main>
    );
  }
  return <CompassShell />;
}

async function boot(setState: (s: BootState) => void): Promise<void> {
  try {
    await loadConfig();
  } catch (err) {
    setState({ kind: 'error', message: err instanceof Error ? err.message : String(err) });
    return;
  }
  if (window.location.pathname === '/auth/callback') {
    try {
      await handleCallback();
      window.history.replaceState({}, '', '/');
      setState({ kind: 'authenticated' });
      return;
    } catch (err) {
      setState({ kind: 'error', message: err instanceof Error ? err.message : String(err) });
      return;
    }
  }
  const tokens = await currentTokens();
  setState({ kind: tokens ? 'authenticated' : 'unauthenticated' });
}

// ---------- Main shell with all 5 views ----------
type ViewId = 'compass' | 'runway' | 'pipeline' | 'projects' | 'decisions';

interface Ctx {
  snapshot: RunwaySnapshot | null;
  refreshSnapshot: () => Promise<void>;
  projects: ProjectRow[];
  setProjects: (rows: ProjectRow[]) => void;
  pipeline: PipelineRow[];
  setPipeline: (rows: PipelineRow[]) => void;
  decisions: DecisionRow[];
  setDecisions: (rows: DecisionRow[]) => void;
}

function CompassShell() {
  const [active, setActive] = useState<ViewId>('compass');
  const [snapshot, setSnapshot] = useState<RunwaySnapshot | null>(null);
  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [pipeline, setPipeline] = useState<PipelineRow[]>([]);
  const [decisions, setDecisions] = useState<DecisionRow[]>([]);
  const [hydrated, setHydrated] = useState(false);
  const [hydrateError, setHydrateError] = useState<string | null>(null);

  const refreshSnapshot = async () => {
    setSnapshot(await getRunwaySnapshot());
  };

  useEffect(() => {
    (async () => {
      try {
        const [snap, ps, pipes, ds] = await Promise.all([
          getRunwaySnapshot(),
          projectsApi.list(),
          pipelineApi.list(),
          decisionsApi.list(),
        ]);
        setSnapshot(snap);

        // First-load seeding: if backend has nothing, push the artifact's
        // SEED so the user gets a useful starting state on day one.
        const seededProjects = ps.length === 0 ? await seed(SEED_PROJECTS, projectsApi.create) : ps;
        const seededPipeline = pipes.length === 0 ? await seed(SEED_PIPELINE, pipelineApi.create) : pipes;
        const seededDecisions = ds.length === 0 ? await seed(SEED_DECISIONS, decisionsApi.create) : ds;

        setProjects(seededProjects);
        setPipeline(seededPipeline);
        setDecisions(seededDecisions);
        setHydrated(true);
      } catch (err) {
        setHydrateError(err instanceof Error ? err.message : String(err));
      }
    })();
  }, []);

  if (hydrateError) {
    return (
      <main className="boot-shell">
        <div className="boot-card">
          <div className="eyebrow">Compass · load failed</div>
          <h1>Couldn't load workspace</h1>
          <p>{hydrateError}</p>
          <button className="btn-quiet" onClick={() => window.location.reload()}>Reload</button>
        </div>
      </main>
    );
  }

  if (!hydrated || !snapshot) {
    return (
      <main className="boot-shell">
        <div className="boot-card">
          <div className="eyebrow">Compass</div>
          <h1>Hydrating workspace…</h1>
          <p>Pulling runway, projects, pipeline, and decisions.</p>
        </div>
      </main>
    );
  }

  const ctx: Ctx = {
    snapshot,
    refreshSnapshot,
    projects,
    setProjects,
    pipeline,
    setPipeline,
    decisions,
    setDecisions,
  };

  const VIEWS: Array<{ id: ViewId; label: string; sub: string; primary?: boolean }> = [
    { id: 'compass', label: 'Compass', sub: 'Strategy with Claude', primary: true },
    { id: 'runway', label: 'Runway', sub: 'Burn · scenarios · floors' },
    { id: 'pipeline', label: 'Pipeline', sub: 'Income paths · velocity' },
    { id: 'projects', label: 'Projects', sub: 'SaaS portfolio · status' },
    { id: 'decisions', label: 'Decisions', sub: 'Reasoning archive' },
  ];

  return (
    <div className="atelier-shell">
      <Sidebar views={VIEWS} active={active} setActive={setActive} />
      <main className="canvas">
        {active === 'compass' && <CompassView ctx={ctx} />}
        {active === 'runway' && <RunwayView ctx={ctx} />}
        {active === 'pipeline' && <PipelineView ctx={ctx} />}
        {active === 'projects' && <ProjectsView ctx={ctx} />}
        {active === 'decisions' && <DecisionsView ctx={ctx} />}
      </main>
    </div>
  );
}

async function seed<T extends { id: string }>(rows: T[], create: (row: T) => Promise<T>): Promise<T[]> {
  const out: T[] = [];
  for (const row of rows) out.push(await create(row));
  return out;
}

// ---------- Sidebar ----------
function Sidebar({
  views,
  active,
  setActive,
}: {
  views: Array<{ id: ViewId; label: string; sub: string; primary?: boolean }>;
  active: ViewId;
  setActive: (id: ViewId) => void;
}) {
  return (
    <aside className="atelier-sidebar">
      <header className="brand">
        <div className="eyebrow" style={{ marginBottom: '0.4rem' }}>
          EMS · {new Date().toLocaleDateString('en-US', { month: '2-digit', year: '2-digit' })}
        </div>
        <div className="brand-name">Compass</div>
        <div className="brand-sub">Strategic command<br />for the unbounded life</div>
      </header>
      <nav className="nav">
        {views.map((v) => (
          <button
            key={v.id}
            onClick={() => setActive(v.id)}
            className={`nav-item ${active === v.id ? 'active' : ''} ${v.primary ? 'primary' : ''}`}
          >
            <div className="nav-label">{v.label}</div>
            <div className="nav-sub">{v.sub}</div>
          </button>
        ))}
      </nav>
      <footer className="sidebar-footer">
        <div className="eyebrow">Status</div>
        <div className="status-line"><span className="status-dot ok" /> AWS · live</div>
        <div className="status-line"><span className="status-dot ok" /> Claude · Sonnet 4.6</div>
        <div className="status-quiet">Last sync · just now</div>
      </footer>
    </aside>
  );
}

// =====================================================================
// COMPASS VIEW (chat)
// =====================================================================
const MODEL_LABELS: Record<string, string> = {
  'claude-sonnet-4-6': 'Sonnet 4.6 (recommended)',
  'claude-opus-4-7': 'Opus 4.7',
  'claude-haiku-4-5-20251001': 'Haiku 4.5',
};
const DEFAULT_MODEL = 'claude-sonnet-4-6';
const CONVERSATION_KEY = 'compass.conversationId';
const MODEL_KEY = 'compass.model';

interface Message { role: 'user' | 'assistant'; content: string; }

function CompassView({ ctx }: { ctx: Ctx }) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [model, setModel] = useState<string>(() => localStorage.getItem(MODEL_KEY) ?? DEFAULT_MODEL);
  const [conversationId, setConversationId] = useState<string | null>(() => localStorage.getItem(CONVERSATION_KEY));
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    localStorage.setItem(MODEL_KEY, model);
  }, [model]);
  useEffect(() => {
    if (conversationId) localStorage.setItem(CONVERSATION_KEY, conversationId);
  }, [conversationId]);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, loading]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const userText = input.trim();
    setMessages((m) => [...m, { role: 'user', content: userText }]);
    setInput('');
    setLoading(true);

    try {
      const resp = await sendChat({
        message: userText,
        conversationId: conversationId ?? undefined,
        model,
      });
      setConversationId(resp.conversationId);
      setMessages((m) => [...m, { role: 'assistant', content: resp.message }]);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setMessages((m) => [...m, { role: 'assistant', content: `Error: ${msg}` }]);
    } finally {
      setLoading(false);
    }
  };

  const clear = () => {
    setMessages([]);
    setConversationId(null);
    localStorage.removeItem(CONVERSATION_KEY);
  };

  const PROMPTS = [
    'If Disney lets me go in 30 days, what is the single highest-leverage move this week?',
    'Rank my 9 projects by probability of revenue in the next 90 days.',
    'Walk me through the math on the §174A election versus consulting income.',
    'What conversation should I have with Dez about the layoff scenario?',
    'Which of the Dez MM SaaS tools should I ship first and why?',
  ];

  return (
    <div>
      <header className="view-header">
        <div className="eyebrow">— Compass</div>
        <h1 className="view-title">Strategy</h1>
        <p className="view-deck">A Claude conversation that has read every other view. Ask anything.</p>
      </header>

      {messages.length === 0 && (
        <div className="atelier-empty">
          <div className="eyebrow" style={{ marginBottom: '1rem' }}>Try one of these</div>
          <div className="prompt-grid">
            {PROMPTS.map((p, i) => (
              <button key={i} className="prompt-card" onClick={() => setInput(p)}>
                <span className="prompt-num">{String(i + 1).padStart(2, '0')}</span>
                <span className="prompt-text">{p}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="chat-thread" ref={scrollRef}>
        {messages.map((m, i) => (
          <div key={i} className={`chat-message ${m.role}`}>
            <div className="chat-role eyebrow">{m.role === 'user' ? 'You' : 'Claude'}</div>
            <div className="chat-content">{m.content}</div>
          </div>
        ))}
        {loading && (
          <div className="chat-message assistant">
            <div className="chat-role eyebrow">Claude</div>
            <div className="chat-content"><span className="thinking">thinking…</span></div>
          </div>
        )}
      </div>

      <div className="chat-composer">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
          placeholder="Ask about runway, projects, the layoff scenario, the §174A play, anything strategic…"
          rows={3}
        />
        <div className="chat-actions">
          <div className="chat-model-row">
            <span className="eyebrow">Model</span>
            <select value={model} onChange={(e) => setModel(e.target.value)}>
              {Object.entries(MODEL_LABELS).map(([id, label]) => (
                <option key={id} value={id}>{label}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn-quiet" onClick={clear}>Clear</button>
            <button className="btn-primary" onClick={() => void send()} disabled={loading || !input.trim()}>
              {loading ? 'Sending…' : 'Send'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// RUNWAY VIEW
// =====================================================================
const FINANCIAL_FIELDS: Array<[keyof RunwaySnapshot, string]> = [
  ['liquid_cash', 'Liquid cash'],
  ['monthly_burn', 'Monthly burn'],
  ['w2_monthly_net', 'W-2 net/mo'],
  ['expected_severance_months', 'Severance months'],
  ['consulting_target', 'Consulting target/mo'],
  ['saas_revenue_target', 'SaaS target/mo'],
];

function RunwayView({ ctx }: { ctx: Ctx }) {
  const { snapshot, refreshSnapshot } = ctx;
  const [scenarioKey, setScenarioKey] = useState<string>('layoff');
  const [editing, setEditing] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  if (!snapshot) return null;
  const scenarios = snapshot.scenarios;
  const active: RunwayScenario = scenarios[scenarioKey] ?? scenarios['layoff'] ?? Object.values(scenarios)[0];

  const persistField = async (field: string, raw: string) => {
    const value = Number(raw) || 0;
    await overridesApi.set(field, value);
    setDrafts((d) => ({ ...d, [field]: '' }));
    await refreshSnapshot();
  };

  const renderRunway = (s: RunwayScenario): string =>
    s.months_runway === null || s.months_runway === Number.POSITIVE_INFINITY
      ? 'unbounded'
      : (s.months_runway as number).toFixed(1) + ' months';

  return (
    <div>
      <header className="view-header">
        <div className="eyebrow">I — Runway</div>
        <h1 className="view-title">Runway</h1>
        <p className="view-deck">Cash, burn, and how long the unbounded life lasts under each path.</p>
      </header>

      <div className="hero-block">
        <div className="hero-figure-row">
          <div>
            <div className="eyebrow">Cash floor today</div>
            <div className="hero-figure numeric">${fmt(snapshot.liquid_cash)}</div>
            <div className="hero-sub">
              Burn ~${fmt(snapshot.monthly_burn)}/mo · {snapshot.account_count} accounts ·
              <span className={`runway-pill ${active.tone}`}>
                {renderRunway(active)} under {active.name.toLowerCase()}
              </span>
            </div>
          </div>
          <button className="btn-quiet" onClick={() => setEditing(!editing)}>
            {editing ? 'Done' : 'Adjust inputs'}
          </button>
        </div>

        {editing && (
          <div className="editor-grid">
            {FINANCIAL_FIELDS.map(([key, label]) => {
              const k = String(key);
              const draft = drafts[k];
              const current = snapshot[key] as number;
              return (
                <label key={k} className="editor-field">
                  <span className="eyebrow">{label}</span>
                  <input
                    type="number"
                    value={draft ?? String(current)}
                    onChange={(e) => setDrafts((d) => ({ ...d, [k]: e.target.value }))}
                    onBlur={(e) => {
                      if (e.target.value !== String(current)) void persistField(k, e.target.value);
                    }}
                  />
                </label>
              );
            })}
          </div>
        )}
      </div>

      <div className="rule-glyph"><span>§</span></div>

      <section>
        <div className="eyebrow" style={{ marginBottom: '1rem' }}>II — Scenarios</div>
        <div className="scenario-tabs">
          {Object.entries(scenarios).map(([key, s]) => (
            <button
              key={key}
              onClick={() => setScenarioKey(key)}
              className={`scenario-tab ${scenarioKey === key ? 'active' : ''}`}
            >
              <div className="scenario-tab-name">{s.name}</div>
              <div className="scenario-tab-runway numeric">{monthsLabel(s.months_runway as number | null)}</div>
            </button>
          ))}
        </div>

        <div className="scenario-detail card">
          <div className="card-eyebrow">{active.name}</div>
          <h2 className="card-title">{active.headline}</h2>
          <p className="scenario-narrative">{active.narrative}</p>

          <div className="grid-3" style={{ marginTop: '1.5rem' }}>
            <div>
              <div className="eyebrow">Monthly inflow</div>
              <div className="numeric scenario-figure">${fmt(active.monthly_in)}</div>
            </div>
            <div>
              <div className="eyebrow">Monthly burn</div>
              <div className="numeric scenario-figure">${fmt(active.monthly_out)}</div>
            </div>
            <div>
              <div className="eyebrow">Net</div>
              <div className={`numeric scenario-figure ${active.net >= 0 ? 'positive' : 'negative'}`}>
                {active.net >= 0 ? '+' : ''}${fmt(active.net)}
              </div>
            </div>
          </div>

          <div className="scenario-notes">
            <div className="eyebrow" style={{ marginBottom: '0.75rem' }}>Assumptions</div>
            <ul>{active.notes.map((n, i) => <li key={i}>{n}</li>)}</ul>
          </div>
        </div>
      </section>
    </div>
  );
}

// =====================================================================
// PIPELINE VIEW
// =====================================================================
function PipelineView({ ctx }: { ctx: Ctx }) {
  const { pipeline, setPipeline } = ctx;
  const totalWeighted = pipeline.reduce((s, p) => s + p.value * p.prob, 0);
  const totalRaw = pipeline.reduce((s, p) => s + p.value, 0);

  const updateLocal = (id: string, key: keyof PipelineRow, value: string | number) => {
    const next = pipeline.map((p) => (p.id === id ? { ...p, [key]: value } as PipelineRow : p));
    setPipeline(next);
  };
  const persist = (id: string) => {
    const row = pipeline.find((p) => p.id === id);
    if (!row) return;
    void pipelineApi.update(id, row).catch((err) => console.error('pipeline update failed', err));
  };

  const addRow = async () => {
    const id = 'p' + Date.now();
    const draft: PipelineRow = { id, source: 'New opportunity', kind: 'TBD', value: 0, prob: 0.5, when: '60d', next: '', tone: 'watch' };
    setPipeline([...pipeline, draft]);
    await pipelineApi.create(draft).catch((err) => console.error('pipeline create failed', err));
  };
  const removeRow = async (id: string) => {
    setPipeline(pipeline.filter((p) => p.id !== id));
    await pipelineApi.remove(id).catch((err) => console.error('pipeline remove failed', err));
  };

  return (
    <div>
      <header className="view-header">
        <div className="eyebrow">II — Pipeline</div>
        <h1 className="view-title">Pipeline</h1>
        <p className="view-deck">Every income path with status and next action. Edit inline.</p>
      </header>

      <div className="hero-block">
        <div className="hero-figure-row">
          <div>
            <div className="eyebrow">Weighted pipeline</div>
            <div className="hero-figure numeric">${fmt(totalWeighted)}</div>
            <div className="hero-sub">
              ${fmt(totalRaw)} raw · {pipeline.length} opportunities · across {new Set(pipeline.map((p) => p.kind)).size} categories
            </div>
          </div>
          <button className="btn-quiet" onClick={() => void addRow()}>+ Add opportunity</button>
        </div>
      </div>

      <div className="rule-glyph"><span>§</span></div>

      <div className="pipeline-grid">
        {pipeline.map((p) => (
          <div key={p.id} className="pipeline-card card lift">
            <div className="pipeline-header">
              <input
                className="pipeline-source"
                value={p.source}
                onChange={(e) => updateLocal(p.id, 'source', e.target.value)}
                onBlur={() => persist(p.id)}
              />
              <span className={`pill ${p.tone}`}>{p.kind}</span>
            </div>
            <div className="pipeline-stats">
              <div>
                <div className="eyebrow">Value</div>
                <div className="numeric pipeline-stat">
                  $<input
                    type="number"
                    value={p.value}
                    onChange={(e) => updateLocal(p.id, 'value', Number(e.target.value) || 0)}
                    onBlur={() => persist(p.id)}
                    className="inline-num"
                  />
                </div>
              </div>
              <div>
                <div className="eyebrow">Probability</div>
                <div className="numeric pipeline-stat">
                  <input
                    type="number" min={0} max={1} step={0.05}
                    value={p.prob}
                    onChange={(e) => updateLocal(p.id, 'prob', Number(e.target.value) || 0)}
                    onBlur={() => persist(p.id)}
                    className="inline-num"
                  />
                </div>
              </div>
              <div>
                <div className="eyebrow">Weighted</div>
                <div className="numeric pipeline-stat">${fmtShort(p.value * p.prob)}</div>
              </div>
              <div>
                <div className="eyebrow">When</div>
                <input
                  value={p.when}
                  onChange={(e) => updateLocal(p.id, 'when', e.target.value)}
                  onBlur={() => persist(p.id)}
                  className="inline-when"
                />
              </div>
            </div>
            <div className="pipeline-next">
              <span className="eyebrow">Next:</span>
              <input
                value={p.next}
                onChange={(e) => updateLocal(p.id, 'next', e.target.value)}
                onBlur={() => persist(p.id)}
                className="inline-next"
              />
            </div>
            <button className="pipeline-remove" onClick={() => void removeRow(p.id)}>×</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// =====================================================================
// PROJECTS VIEW
// =====================================================================
const TIERS = {
  production: { label: 'Active Production', desc: 'Live · primary §174A foundation' },
  commercial: { label: 'Commercial · in dev', desc: 'Building toward customer revenue' },
  platform: { label: 'Platform R&D', desc: 'Infrastructure + personal tools' },
} as const;
const TIER_ORDER: Array<keyof typeof TIERS> = ['production', 'commercial', 'platform'];

function ProjectsView({ ctx }: { ctx: Ctx }) {
  const { projects, setProjects } = ctx;

  const updateLocal = (id: string, key: keyof ProjectRow, value: string) => {
    const next = projects.map((p) => (p.id === id ? { ...p, [key]: value } as ProjectRow : p));
    setProjects(next);
  };
  const persist = (id: string) => {
    const row = projects.find((p) => p.id === id);
    if (!row) return;
    void projectsApi.update(id, row).catch((err) => console.error('projects update failed', err));
  };

  const grouped = TIER_ORDER.map((t) => ({
    tier: t,
    label: TIERS[t].label,
    desc: TIERS[t].desc,
    items: projects.filter((p) => p.tier === t),
  }));

  return (
    <div>
      <header className="view-header">
        <div className="eyebrow">III — Projects</div>
        <h1 className="view-title">Portfolio</h1>
        <p className="view-deck">{projects.length} active projects, organized by tax-defensibility tier.</p>
      </header>

      {grouped.map((group) => (
        <section key={group.tier} className="project-tier">
          <div className="tier-header">
            <h2 className="tier-label">{group.label}</h2>
            <div className="tier-desc">{group.desc} · {group.items.length}</div>
          </div>
          <div className="project-grid">
            {group.items.map((p) => (
              <div key={p.id} className="project-card card lift">
                <div className="project-card-header">
                  <h3 className="project-name">{p.name}</h3>
                  <span className={`pill ${p.status === 'live' ? 'ok' : p.status === 'paused' ? 'warn' : 'watch'}`}>{p.status}</span>
                </div>
                <div className="project-meta">
                  <div><span className="eyebrow">Customer</span><div className="meta-value">{p.customer}</div></div>
                  <div><span className="eyebrow">Stack</span><div className="meta-value">{p.stack}</div></div>
                </div>
                <div className="project-next">
                  <div className="eyebrow">Next action</div>
                  <input
                    value={p.next}
                    onChange={(e) => updateLocal(p.id, 'next', e.target.value)}
                    onBlur={() => persist(p.id)}
                    className="inline-next"
                  />
                </div>
                <p className="project-notes">{p.notes}</p>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

// =====================================================================
// DECISIONS VIEW
// =====================================================================
function DecisionsView({ ctx }: { ctx: Ctx }) {
  const { decisions, setDecisions } = ctx;

  const updateLocal = (id: string, key: keyof DecisionRow, value: string) => {
    const next = decisions.map((d) => (d.id === id ? { ...d, [key]: value } as DecisionRow : d));
    setDecisions(next);
  };
  const persist = (id: string) => {
    const row = decisions.find((d) => d.id === id);
    if (!row) return;
    void decisionsApi.update(id, row).catch((err) => console.error('decisions update failed', err));
  };

  const addDecision = async () => {
    const id = 'd' + Date.now();
    const today = new Date().toISOString().split('T')[0];
    const draft: DecisionRow = { id, date: today, title: 'New decision', reasoning: '', verdict: 'Pending', tone: 'pending' };
    setDecisions([draft, ...decisions]);
    await decisionsApi.create(draft).catch((err) => console.error('decisions create failed', err));
  };
  const removeDecision = async (id: string) => {
    setDecisions(decisions.filter((d) => d.id !== id));
    await decisionsApi.remove(id).catch((err) => console.error('decisions remove failed', err));
  };

  return (
    <div>
      <header className="view-header">
        <div className="eyebrow">IV — Decisions</div>
        <h1 className="view-title">Reasoning</h1>
        <p className="view-deck">Why you chose what you chose. Future-you will thank present-you.</p>
      </header>

      <div className="hero-block">
        <div className="hero-figure-row">
          <div>
            <div className="eyebrow">Tracked decisions</div>
            <div className="hero-figure numeric">{decisions.length}</div>
          </div>
          <button className="btn-quiet" onClick={() => void addDecision()}>+ Capture decision</button>
        </div>
      </div>

      <div className="decisions-list">
        {decisions.map((d) => (
          <article key={d.id} className="decision-item card">
            <div className="decision-meta">
              <span className="eyebrow">{d.date}</span>
              <span className={`pill ${d.tone === 'firm' ? 'ok' : d.tone === 'pending' ? 'watch' : 'warn'}`}>{d.verdict}</span>
            </div>
            <input
              value={d.title}
              onChange={(e) => updateLocal(d.id, 'title', e.target.value)}
              onBlur={() => persist(d.id)}
              className="decision-title-input"
            />
            <textarea
              value={d.reasoning}
              onChange={(e) => updateLocal(d.id, 'reasoning', e.target.value)}
              onBlur={() => persist(d.id)}
              className="decision-reasoning"
              rows={3}
              placeholder="Why this, not the alternatives?"
            />
            <button className="decision-remove" onClick={() => void removeDecision(d.id)}>Remove</button>
          </article>
        ))}
      </div>
    </div>
  );
}
